/**
 * SPのハンバーガーメニューを開閉する
 *
 * @description ボタンで .is-open を付け外しし、開いている間は背景のスクロールを止める。
 *              見た目（フェード・丸の拡大）はCSS側が持っていて、ここは状態の管理だけ。
 *              ロックは overflow:hidden で行う。bodyをposition:fixedで固定する方法だと
 *              解除した瞬間にスクロール位置が0に戻るため、閉じたときに元の位置へ
 *              戻る動きが見えてしまう。overflowなら位置がそのまま残るので復元がいらない。
 *              メニューはSPにしか出ないので、PCで実行されても何も起きない。
 */

/** 開いている間に付けるクラス */
const OPEN_CLASS = "is-open"

/** フェードアウトのtransitionendが来なかったときの保険（CSSは0.1s+0.4s） */
const FADE_OUT_FALLBACK = 700

const menu = document.querySelector<HTMLElement>(".header__menu")
const openBtn = document.querySelector<HTMLButtonElement>(".header__menuBtn")
const closeBtn = document.querySelector<HTMLButtonElement>(".header__menuClose")

/** いま背景のスクロールを止めているか */
let isScrollLocked = false

/** 閉じたあとに飛ぶページ内アンカー（メニューの項目を押したとき） */
let pendingHash = ""

/** フェードアウト待ちのタイマーID */
let fadeOutTimer = 0

/** フェードアウト待ちのtransitionendハンドラ */
let fadeOutHandler: ((e: TransitionEvent) => void) | null = null

/**
 * 背景のスクロールを止める
 * @returns {void}
 * @description スクロール位置は動かさない。iOSはbody側だけだと効かないことがあるので
 *              html（documentElement）にも掛ける。
 */
const lockScroll = (): void => {
  if (isScrollLocked) {
    return
  }

  isScrollLocked = true
  document.documentElement.style.overflow = "hidden"
  document.body.style.overflow = "hidden"
}

/**
 * 背景のスクロールを元に戻す
 * @returns {void}
 * @description 位置は止めた時のまま残っているので、戻す処理はしない。
 */
const unlockScroll = (): void => {
  if (!isScrollLocked) {
    return
  }

  isScrollLocked = false
  document.documentElement.style.overflow = ""
  document.body.style.overflow = ""
}

/**
 * ページ内アンカーへ即座に移動する
 * @param {string} hash - 移動先（#付き）
 * @returns {void}
 * @description html側が scroll-behavior:smooth なので、そのままだと長い距離を
 *              スクロールする様子が見えてしまう。一時的に auto へ上書きして飛ばす。
 */
const jumpToHash = (hash: string): void => {
  const target = document.querySelector(hash)

  if (!target) {
    return
  }

  const root = document.documentElement
  const previousBehavior = root.style.scrollBehavior

  root.style.scrollBehavior = "auto"
  // scroll-margin-top（ヘッダー分のオフセット）を効かせたいのでscrollIntoViewを使う
  target.scrollIntoView({ block: "start" })
  history.replaceState(null, "", hash)

  requestAnimationFrame(() => {
    root.style.scrollBehavior = previousBehavior
  })
}

/**
 * フェードアウト待ちを取り消す
 * @returns {void}
 */
const cancelFadeOutWait = (): void => {
  if (fadeOutTimer) {
    window.clearTimeout(fadeOutTimer)
    fadeOutTimer = 0
  }

  if (fadeOutHandler && menu) {
    menu.removeEventListener("transitionend", fadeOutHandler)
    fadeOutHandler = null
  }
}

/**
 * パネルが消えきってからロックを解除する
 * @returns {void}
 */
const unlockAfterFadeOut = (): void => {
  if (!menu) {
    return
  }

  /**
   * 待ちを終えてロックを解除し、行き先があれば移動する
   * @returns {void}
   */
  const finish = (): void => {
    cancelFadeOutWait()
    unlockScroll()

    if (pendingHash) {
      jumpToHash(pendingHash)
      pendingHash = ""
    }
  }

  // アニメーションを切っている環境は待つ意味がないのですぐ解除する
  if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
    finish()
    return
  }

  fadeOutHandler = (e: TransitionEvent): void => {
    if (e.target === menu && e.propertyName === "opacity") {
      finish()
    }
  }

  menu.addEventListener("transitionend", fadeOutHandler)
  fadeOutTimer = window.setTimeout(finish, FADE_OUT_FALLBACK)
}

/**
 * メニューを開く
 * @returns {void}
 * @example
 * openMenu()
 */
const openMenu = (): void => {
  if (!menu || menu.classList.contains(OPEN_CLASS)) {
    return
  }

  // 閉じ途中に開き直したときは、あとから走る解除・移動を止める
  cancelFadeOutWait()
  pendingHash = ""

  lockScroll()
  menu.classList.add(OPEN_CLASS)
  menu.setAttribute("aria-hidden", "false")
  openBtn?.setAttribute("aria-expanded", "true")
  closeBtn?.focus()
}

/**
 * メニューを閉じる
 * @returns {void}
 * @example
 * closeMenu()
 */
const closeMenu = (): void => {
  if (!menu || !menu.classList.contains(OPEN_CLASS)) {
    return
  }

  menu.classList.remove(OPEN_CLASS)
  menu.setAttribute("aria-hidden", "true")
  openBtn?.setAttribute("aria-expanded", "false")
  unlockAfterFadeOut()
}

if (menu && openBtn) {
  openBtn.addEventListener("click", openMenu)
  closeBtn?.addEventListener("click", closeMenu)

  menu.querySelectorAll<HTMLAnchorElement>("a").forEach((link) => {
    link.addEventListener("click", (e) => {
      const href = link.getAttribute("href") ?? ""

      // ページ内アンカーはパネルが消えてから移動させる
      if (href.startsWith("#")) {
        e.preventDefault()
        pendingHash = href
      }

      closeMenu()
    })
  })

  document.addEventListener("keydown", (e) => {
    if (e.key === "Escape") {
      closeMenu()
      openBtn.focus()
    }
  })
}
